<template>
  <form @submit.prevent="handleSubmit" class="h-auto max-h-[800px] flex flex-col">
    <!-- Main Form Content -->
    <div class="flex-1 overflow-y-auto px-4 py-2">
      <!-- Form Sections Container -->
      <div class="space-y-3 divide-y divide-gray-200 dark:divide-dark-600">
        <!-- Top Section: Basic Info + Receipt -->
        <div class="grid grid-cols-12 gap-3">
          <!-- Left Column -->
          <div class="col-span-8 space-y-3">
            <h3 class="text-sm font-medium text-gray-900 dark:text-dark-100">Basic Information</h3>
            
            <!-- First Row -->
            <div class="grid grid-cols-4 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-dark-300">Date *</label>
                <input
                  type="date"
                  v-model="formData.expense_date"
                  required
                  class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-dark-300">Group *</label>
                <select
                  v-model="formData.expense_group_id"
                  required
                  class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
                >
                  <option value="">Select group</option>
                  <option v-for="group in expenseGroups" :key="group.id" :value="group.id">
                    {{ group.name }}{{ group.is_admin_only ? ' 🔒' : '' }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-dark-300">Department *</label>
                <select
                  v-model="formData.department_id"
                  required
                  class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
                >
                  <option value="">Select dept</option>
                  <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                    {{ dept.name }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-dark-300">Supplier *</label>
                <div class="relative">
                  <input
                    v-model="supplierSearch"
                    type="text"
                    required
                    placeholder="Search supplier..."
                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100 placeholder-gray-500 dark:placeholder-dark-400"
                    @focus="showSupplierDropdown = true"
                    @input="filterSuppliers"
                  />
                  
                  <!-- Supplier Dropdown -->
                  <div
                    v-if="showSupplierDropdown && filteredSuppliers.length > 0"
                    class="absolute z-10 w-full mt-1 bg-white dark:bg-dark-800 shadow-lg rounded-md border border-gray-300 dark:border-dark-600 max-h-60 overflow-y-auto"
                  >
                    <div
                      v-for="supplier in filteredSuppliers"
                      :key="supplier.id"
                      @click="selectSupplier(supplier)"
                      class="px-3 py-2 hover:bg-gray-100 dark:hover:bg-dark-700 cursor-pointer text-sm text-gray-900 dark:text-dark-100"
                    >
                      {{ supplier.name }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Payment Method Row -->
            <div class="grid grid-cols-4 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-700 dark:text-dark-300">Payment Method</label>
                <select
                  v-model="formData.payment_method"
                  class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
                >
                  <option value="">Select method</option>
                  <option value="cash">Cash</option>
                  <option value="online">Online Payment</option>
                </select>
              </div>

              <!-- Dynamic Payment Fields -->
              <div v-if="formData.payment_method && formData.payment_method !== 'cash'" class="col-span-3">
                <!-- Online Payment Fields -->
                <div v-if="formData.payment_method === 'online'" class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-dark-300">Payment Platform</label>
                    <input
                      v-model="formData.online_platform"
                      type="text"
                      required
                      placeholder="e.g., GCash, Maya, etc."
                      class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100 placeholder-gray-500 dark:placeholder-dark-400"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-dark-300">
                      Reference #
                      <i v-if="isProcessingOCR" class="fas fa-spinner fa-spin ml-1 text-blue-500"></i>
                    </label>
                    <input
                      v-model="formData.transaction_reference"
                      type="text"
                      required
                      placeholder="Enter reference"
                      class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100 placeholder-gray-500 dark:placeholder-dark-400"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div>
              <label class="block text-xs font-medium text-gray-700 dark:text-dark-300">Description/Notes</label>
              <textarea
                v-model="formData.notes"
                rows="1"
                placeholder="Enter description and notes..."
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100 placeholder-gray-500 dark:placeholder-dark-400"
              ></textarea>
            </div>
          </div>

          <!-- Right Column: Image Uploads -->
          <div class="col-span-4 space-y-3">
            <!-- Receipt Upload -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Receipt Image*</label>
              <div
                class="border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer hover:border-blue-400 dark:hover:border-blue-400"
                :class="[
                    imagePreview || formData.receipt_image_url ? 
                      'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20' : 
                      isDragging ? 'border-blue-400 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600'
                  ]"
                @click="$refs.imageInput.click()"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleImageDrop"
                @paste.prevent="handleReceiptImagePaste"
                tabindex="0"
                @keydown.prevent
                ref="receiptPasteArea"
              >
                <input
                  ref="imageInput"
                  type="file"
                  class="hidden"
                  accept="image/jpeg,image/png,image/gif"
                  @change="handleImageUpload"
                />
                <div v-if="!imagePreview && !formData.receipt_image_url" class="space-y-2">
                  <i class="fas fa-image text-xl text-gray-400 dark:text-dark-400"></i>
                  <div class="text-sm text-gray-600 dark:text-dark-400 space-y-1">
                    <p class="font-medium text-xs">Receipt upload:</p>
                    <div class="flex items-center justify-center gap-3 text-xs">
                      <span class="flex items-center gap-1">
                        <i class="fas fa-mouse-pointer text-blue-500"></i>
                        Browse
                      </span>
                      <span class="flex items-center gap-1 text-green-600 dark:text-green-400 font-semibold">
                        <i class="fas fa-paste text-green-500"></i>
                        Ctrl+V
                      </span>
                      <span class="flex items-center gap-1">
                        <i class="fas fa-hand-paper text-purple-500"></i>
                        Drag
                      </span>
                    </div>
                    <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-600 rounded p-1">
                      <p class="text-green-700 dark:text-green-300 text-xs text-center">
                        <i class="fas fa-lightbulb mr-1"></i>
                        Copy receipt & press Ctrl+V
                      </p>
                    </div>
                    <p class="text-xs text-gray-500 dark:text-dark-400 text-center">PNG, JPG, GIF (10MB max)</p>
                  </div>
                </div>
                <img 
                  v-else
                  :src="getReceiptUrl"
                  class="w-full h-24 object-cover rounded-md"
                  alt="Receipt preview"
                />
              </div>
            </div>

            <!-- Payment Proof (conditional) -->
            <div v-if="formData.payment_method && formData.payment_method !== 'cash'">
              <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Payment Proof</label>
              <div 
                class="border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer hover:border-blue-400 dark:hover:border-blue-400"
                :class="[
                  paymentImagePreview || formData.payment_proof_url ? 
                    'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20' : 
                    isPaymentDragging ? 'border-blue-400 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 hover:bg-gray-100 dark:hover:bg-dark-600'
                ]"
                @click="$refs.paymentImageInput.click()"
                @dragover.prevent="isPaymentDragging = true"
                @dragleave.prevent="isPaymentDragging = false"
                @drop.prevent="handlePaymentImageDrop"
                @paste.prevent="handlePaymentImagePaste"
                tabindex="0"
                @keydown.prevent
                ref="paymentPasteArea"
              >
                <input
                  ref="paymentImageInput"
                  type="file"
                  class="hidden"
                  accept="image/jpeg,image/png,image/gif"
                  @change="handlePaymentImageUpload"
                />
                <div v-if="!paymentImagePreview && !formData.payment_proof_url" class="space-y-2">
                  <i class="fas fa-image text-xl text-gray-400 dark:text-dark-400"></i>
                  <div class="text-sm text-gray-600 dark:text-dark-400 space-y-1">
                    <p class="font-medium text-xs">Payment proof upload:</p>
                    <div class="flex items-center justify-center gap-3 text-xs">
                      <span class="flex items-center gap-1">
                        <i class="fas fa-mouse-pointer text-blue-500"></i>
                        Browse
                      </span>
                      <span class="flex items-center gap-1 text-green-600 dark:text-green-400 font-semibold">
                        <i class="fas fa-paste text-green-500"></i>
                        Ctrl+V
                      </span>
                      <span class="flex items-center gap-1">
                        <i class="fas fa-hand-paper text-purple-500"></i>
                        Drag
                      </span>
                    </div>
                    <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-600 rounded p-1">
                      <p class="text-green-700 dark:text-green-300 text-xs text-center">
                        <i class="fas fa-lightbulb mr-1"></i>
                        Auto-extracts platform & reference
                      </p>
                    </div>
                    <p class="text-xs text-gray-500 dark:text-dark-400 text-center">PNG, JPG, GIF (10MB max)</p>
                  </div>
                </div>
                <img 
                  v-else
                  :src="getPaymentProofUrl"
                  class="w-full h-24 object-cover rounded-md"
                  alt="Payment proof preview"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Items Section -->
        <div class="pt-2">
          <div class="flex justify-between items-center mb-1">
            <h3 class="text-sm font-medium text-gray-900 dark:text-dark-100">Items</h3>
            <button
              type="button"
              @click="addItem"
              :disabled="!canAddItems"
              class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fas fa-plus mr-1"></i>
              Add Item
            </button>
          </div>

          <!-- Items Table -->
          <div class="border border-gray-300 dark:border-dark-600 rounded-lg shadow overflow-hidden bg-white dark:bg-dark-800">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-dark-600">
              <thead class="bg-gray-50 dark:bg-dark-700">
                <tr>
                  <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider w-1/3">Product</th>
                  <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider w-20">Qty</th>
                  <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider w-28">Unit Price</th>
                  <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider w-28">Total</th>
                  <th scope="col" class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Notes</th>
                  <th scope="col" class="relative px-4 py-2 w-16">
                    <span class="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-600">
                <tr v-for="(item, index) in formData.items" :key="index" class="hover:bg-gray-50 dark:hover:bg-dark-700">
                  <td class="px-4 py-2">
                    <select
                      v-model="item.product_id"
                      required
                      class="block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
                      @change="updateUnitPrice(index)"
                    >
                      <option value="">Select product</option>
                      <option v-for="product in supplierProducts" :key="product.id" :value="product.id">
                        {{ product.name }}{{ selectedGroup && selectedGroup.is_admin_only ? ' 🔒' : '' }}
                      </option>
                    </select>
                  </td>
                  <td class="px-4 py-2">
                    <input
                      type="number"
                      v-model="item.quantity"
                      required
                      min="0.01"
                      step="0.01"
                      class="block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
                      @input="calculateItemTotal(index)"
                    />
                  </td>
                  <td class="px-4 py-2">
                    <div class="relative rounded-md shadow-sm">
                      <div class="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                        <span class="text-gray-500 dark:text-dark-400 sm:text-sm">₱</span>
                      </div>
                      <input
                        type="number"
                        v-model="item.unit_price"
                        required
                        min="0"
                        step="0.01"
                        class="block w-full pl-6 rounded-md border-gray-300 dark:border-dark-600 focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
                        @input="calculateItemTotal(index)"
                      />
                    </div>
                  </td>
                  <td class="px-4 py-2">
                    <div class="text-sm font-medium text-gray-900 dark:text-dark-100">₱{{ formatCurrency(item.total_cost) }}</div>
                  </td>
                  <td class="px-4 py-2">
                    <input
                      type="text"
                      v-model="item.notes"
                      class="block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100 placeholder-gray-500 dark:placeholder-dark-400"
                      placeholder="Item notes..."
                    />
                  </td>
                  <td class="px-4 py-2 text-right">
                    <button
                      type="button"
                      @click="removeItem(index)"
                      class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 focus:outline-none"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="formData.items.length === 0">
                  <td colspan="6" class="px-4 py-2 text-center text-gray-500 dark:text-dark-400">
                    No items added yet. Click "Add Item" to start adding items.
                  </td>
                </tr>
              </tbody>
              <tfoot class="bg-gray-50 dark:bg-dark-700">
                <tr>
                  <td colspan="3" class="px-4 py-1 text-right font-medium text-gray-700 dark:text-dark-100">Total Amount:</td>
                  <td class="px-4 py-1">
                    <div class="text-base font-bold text-gray-900 dark:text-dark-100">₱{{ formatCurrency(totalAmount) }}</div>
                  </td>
                  <td colspan="2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Form Actions -->
    <div class="flex justify-end space-x-3 bg-white dark:bg-dark-800 py-2 px-4 border-t border-gray-200 dark:border-dark-600 mt-2">
      <button
        type="button"
        @click="$emit('cancel')"
        class="px-3 py-1 border border-gray-300 dark:border-dark-600 rounded-md text-sm font-medium text-gray-700 dark:text-dark-300 hover:bg-gray-50 dark:hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Cancel
      </button>
      <button
        type="submit"
        :disabled="submitDisabled"
        :class="[
          'px-4 py-1 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
          submitDisabled ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
        ]"
      >
        <i v-if="isSubmitting" class="fas fa-spinner fa-spin mr-1"></i>
        {{ editMode ? 'Update' : 'Create' }} Expense
      </button>
    </div>

    <!-- Add submission status message for better UX -->
    <p v-if="isSubmitting" class="text-xs text-blue-600 dark:text-blue-400 text-center mt-1">
      <i class="fas fa-info-circle mr-1"></i>
      Processing your submission, please wait...
    </p>
  </form>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useApi } from '@/composables/useApi';
import { useToast } from '@/composables/useToast';
import { getImageUrl } from '@/utils/imageUtils';

const api = useApi();
const toast = useToast();

// Add these submission state variables
const isSubmitting = ref(false);
const submitDisabled = ref(false);
const isDragging = ref(false);
const isPaymentDragging = ref(false);
const isProcessingOCR = ref(false);

const props = defineProps({
  expense: {
    type: Object,
    default: () => null
  }
});

const emit = defineEmits(['submit', 'cancel']);

const suppliers = ref([]);
const supplierProducts = ref([]);
const expenseGroups = ref([]);
const departments = ref([]);
const editMode = ref(false);

// Supplier autocomplete state
const supplierSearch = ref('');
const filteredSuppliers = ref([]);
const showSupplierDropdown = ref(false);

const formData = ref({
  expense_group_id: '',
  department_id: '',
  supplier_id: '',
  expense_date: new Date().toISOString().slice(0, 10),
  receipt_image_url: '',
  payment_proof_url: null,
  notes: '',
  payment_method: '',
  online_platform: '',
  transaction_reference: '',
  items: []
});

const totalAmount = computed(() => {
  return formData.value.items.reduce((sum, item) => sum + (parseFloat(item.total_cost) || 0), 0);
});

const canAddItems = computed(() => {
  return formData.value.expense_group_id && 
         formData.value.department_id && 
         formData.value.supplier_id;
});

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const addItem = () => {
  formData.value.items.push({
    product_id: '',
    quantity: 1,
    unit_price: 0,
    total_cost: 0,
    notes: ''
  });
};

const removeItem = (index) => {
  formData.value.items.splice(index, 1);
};

const calculateItemTotal = (index) => {
  const item = formData.value.items[index];
  item.total_cost = item.quantity * item.unit_price;
  console.log('[ExpenseFormWithItems] Calculating total:', { quantity: item.quantity, unit_price: item.unit_price, total_cost: item.total_cost });
};

const updateUnitPrice = (index) => {
  const item = formData.value.items[index];
  const product = supplierProducts.value.find(p => p.id === item.product_id);
  if (product) {
    item.unit_price = product.unit_cost;
    calculateItemTotal(index);
  }
};

const imageInput = ref(null);
const imagePreview = ref(null);

const handleImageDrop = (event) => {
  isDragging.value = false;
  
  try {
    const files = event.dataTransfer.files;
    
    if (files.length === 0) {
      toast.error('No files were dropped. Please try again.');
      return;
    }
    
    if (files.length > 1) {
      toast.error('Please drop only one image file at a time.');
      return;
    }
    
    const file = files[0];
    console.log('Receipt image dropped:', file.name, file.type, file.size);
    validateAndProcessImage(file);
    toast.success('Receipt image dropped successfully!');
  } catch (error) {
    console.error('Error handling drop event:', error);
    toast.error('Failed to process dropped image. Please try again.');
  }
};

const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    validateAndProcessImage(file);
  }
};

// Handle paste event for receipt images
const handleReceiptImagePaste = async (event) => {
  console.log('Receipt paste event triggered:', event);
  event.preventDefault();
  event.stopPropagation();
  
  try {
    let imageFound = false;
    
    // First, try to get image from clipboardData (works for most cases)
    if (event.clipboardData && event.clipboardData.items) {
      console.log('Checking clipboardData items:', event.clipboardData.items.length);
      
      for (let i = 0; i < event.clipboardData.items.length; i++) {
        const item = event.clipboardData.items[i];
        console.log('Clipboard item:', item.kind, item.type);
        
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            console.log('Receipt image pasted from clipboardData:', file.name || 'clipboard-image', file.type, file.size);
            validateAndProcessImage(file);
            toast.success('Receipt image pasted successfully!');
            imageFound = true;
            return;
          }
        }
      }
    }
    
    // Second, try to get image from files (for drag & drop compatibility)
    if (event.clipboardData && event.clipboardData.files && event.clipboardData.files.length > 0) {
      console.log('Checking clipboardData files:', event.clipboardData.files.length);
      
      for (let i = 0; i < event.clipboardData.files.length; i++) {
        const file = event.clipboardData.files[i];
        console.log('Clipboard file:', file.name, file.type, file.size);
        
        if (file.type.startsWith('image/')) {
          console.log('Receipt image found in files');
          validateAndProcessImage(file);
          toast.success('Receipt image pasted successfully!');
          imageFound = true;
          return;
        }
      }
    }
    
    // Third, try modern Clipboard API (for additional browser support)
    if (!imageFound && navigator.clipboard && navigator.clipboard.read) {
      try {
        console.log('Trying modern Clipboard API...');
        const clipboardContents = await navigator.clipboard.read();
        console.log('Clipboard contents:', clipboardContents);
        
        for (const item of clipboardContents) {
          console.log('Clipboard item types:', item.types);
          for (const type of item.types) {
            if (type.startsWith('image/')) {
              const blob = await item.getType(type);
              const file = new File([blob], 'pasted-receipt-image', { type: blob.type });
              console.log('Receipt image from Clipboard API:', file.type, file.size);
              validateAndProcessImage(file);
              toast.success('Receipt image pasted successfully!');
              imageFound = true;
              return;
            }
          }
        }
      } catch (clipboardError) {
        console.warn('Clipboard API failed:', clipboardError);
      }
    }
    
    if (!imageFound) {
      console.log('No image found in clipboard');
      toast.error('No image found in clipboard. Try copying an image first (Ctrl+C), then paste it here (Ctrl+V).');
    }
  } catch (error) {
    console.error('Error handling paste event:', error);
    toast.error('Failed to paste image. Please try copying the image again.');
  }
};

const validateAndProcessImage = async (file) => {
  // Validate file type and size
  const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
  if (!validTypes.includes(file.type)) {
    toast.error('Please select a valid image file (JPG, PNG, or GIF)');
    return;
  }

  if (file.size > 10 * 1024 * 1024) { // 10MB limit
    toast.error('Image file size must be less than 10MB');
    return;
  }

  try {
    // Create FormData for file upload
    const uploadFormData = new FormData();
    uploadFormData.append('image', file);

    const response = await api.post('/upload', uploadFormData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      params: {
        type: 'expense'  // Specify that this is an expense upload
      }
    });

    // Clear previous preview if exists
    if (imagePreview.value && imagePreview.value.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview.value);
    }

    // Set new preview and URL
    imagePreview.value = URL.createObjectURL(file);
    formData.value.receipt_image_url = response.data.url;

    console.log('[ExpenseFormWithItems] Image uploaded successfully:', response.data.url);
    toast.success('Receipt image uploaded successfully!');
  } catch (error) {
    console.error('Error uploading image:', error);
    toast.error('Failed to upload image');
  }
};

const removeImage = () => {
  // Clear the file input
  if (imageInput.value) {
    imageInput.value.value = '';
  }

  // Clear the preview if it's a blob URL
  if (imagePreview.value && imagePreview.value.startsWith('blob:')) {
    URL.revokeObjectURL(imagePreview.value);
  }

  // Reset all image-related values
  imagePreview.value = null;
  formData.value.receipt_image_url = null;
};

const loadSupplierProducts = async () => {
  if (!formData.value.supplier_id) return;
  
  try {
    const response = await api.get(`/suppliers/${formData.value.supplier_id}/products`);
    supplierProducts.value = response.data;
  } catch (error) {
    console.error('Error loading supplier products:', error);
    toast.error('Failed to load supplier products');
  }
};

// Supplier autocomplete methods
const filterSuppliers = () => {
  if (!supplierSearch.value.trim()) {
    filteredSuppliers.value = suppliers.value;
  } else {
    const searchTerm = supplierSearch.value.toLowerCase();
    filteredSuppliers.value = suppliers.value.filter(supplier =>
      supplier.name.toLowerCase().includes(searchTerm)
    );
  }
  showSupplierDropdown.value = true;
};

const selectSupplier = (supplier) => {
  formData.value.supplier_id = supplier.id;
  supplierSearch.value = supplier.name;
  showSupplierDropdown.value = false;
  loadSupplierProducts();
};

// Close dropdown when clicking outside
const closeSupplierDropdown = (event) => {
  if (!event.target.closest('.relative')) {
    showSupplierDropdown.value = false;
  }
};

const getReceiptUrl = computed(() => {
  if (imagePreview.value) return imagePreview.value;
  if (formData.value.receipt_image_url) {
    return getImageUrl(formData.value.receipt_image_url);
  }
  return '';
});

const paymentImageInput = ref(null);
const paymentImagePreview = ref(null);

const handlePaymentImageDrop = (event) => {
  isPaymentDragging.value = false;
  
  try {
    const files = event.dataTransfer.files;
    
    if (files.length === 0) {
      toast.error('No files were dropped. Please try again.');
      return;
    }
    
    if (files.length > 1) {
      toast.error('Please drop only one image file at a time.');
      return;
    }
    
    const file = files[0];
    console.log('Payment proof image dropped:', file.name, file.type, file.size);
    validateAndProcessPaymentImage(file);
    toast.success('Payment proof image dropped successfully!');
  } catch (error) {
    console.error('Error handling payment proof drop event:', error);
    toast.error('Failed to process dropped payment proof image. Please try again.');
  }
};

// Handle paste event for payment proof images
const handlePaymentImagePaste = async (event) => {
  console.log('Payment proof paste event triggered:', event);
  event.preventDefault();
  event.stopPropagation();
  
  try {
    let imageFound = false;
    
    // First, try to get image from clipboardData (works for most cases)
    if (event.clipboardData && event.clipboardData.items) {
      console.log('Checking clipboardData items for payment proof:', event.clipboardData.items.length);
      
      for (let i = 0; i < event.clipboardData.items.length; i++) {
        const item = event.clipboardData.items[i];
        console.log('Payment proof clipboard item:', item.kind, item.type);
        
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            console.log('Payment proof image pasted from clipboardData:', file.name || 'clipboard-image', file.type, file.size);
            validateAndProcessPaymentImage(file);
            toast.success('Payment proof image pasted successfully!');
            imageFound = true;
            return;
          }
        }
      }
    }
    
    // Second, try to get image from files (for drag & drop compatibility)
    if (event.clipboardData && event.clipboardData.files && event.clipboardData.files.length > 0) {
      console.log('Checking clipboardData files for payment proof:', event.clipboardData.files.length);
      
      for (let i = 0; i < event.clipboardData.files.length; i++) {
        const file = event.clipboardData.files[i];
        console.log('Payment proof clipboard file:', file.name, file.type, file.size);
        
        if (file.type.startsWith('image/')) {
          console.log('Payment proof image found in files');
          validateAndProcessPaymentImage(file);
          toast.success('Payment proof image pasted successfully!');
          imageFound = true;
          return;
        }
      }
    }
    
    // Third, try modern Clipboard API (for additional browser support)
    if (!imageFound && navigator.clipboard && navigator.clipboard.read) {
      try {
        console.log('Trying modern Clipboard API for payment proof...');
        const clipboardContents = await navigator.clipboard.read();
        console.log('Payment proof clipboard contents:', clipboardContents);
        
        for (const item of clipboardContents) {
          console.log('Payment proof clipboard item types:', item.types);
          for (const type of item.types) {
            if (type.startsWith('image/')) {
              const blob = await item.getType(type);
              const file = new File([blob], 'pasted-payment-proof-image', { type: blob.type });
              console.log('Payment proof image from Clipboard API:', file.type, file.size);
              validateAndProcessPaymentImage(file);
              toast.success('Payment proof image pasted successfully!');
              imageFound = true;
              return;
            }
          }
        }
      } catch (clipboardError) {
        console.warn('Payment proof Clipboard API failed:', clipboardError);
      }
    }
    
    if (!imageFound) {
      console.log('No image found in clipboard for payment proof');
      toast.error('No image found in clipboard. Try copying an image first (Ctrl+C), then paste it here (Ctrl+V).');
    }
  } catch (error) {
    console.error('Error handling payment proof paste event:', error);
    toast.error('Failed to paste payment proof image. Please try copying the image again.');
  }
};

const handlePaymentImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    validateAndProcessPaymentImage(file);
  }
};

const validateAndProcessPaymentImage = async (file) => {
  // Validate file type and size
  const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
  if (!validTypes.includes(file.type)) {
    toast.error('Please select a valid image file (JPG, PNG, or GIF)');
    return;
  }

  if (file.size > 10 * 1024 * 1024) { // 10MB limit
    toast.error('Image file size must be less than 10MB');
    return;
  }

  try {
    // Create FormData for file upload
    const uploadFormData = new FormData();
    uploadFormData.append('image', file);

    const response = await api.post('/upload', uploadFormData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      params: {
        type: 'payment'  // Specify that this is a payment proof upload
      }
    });

    // Clear previous preview if exists
    if (paymentImagePreview.value && paymentImagePreview.value.startsWith('blob:')) {
      URL.revokeObjectURL(paymentImagePreview.value);
    }

    // Set new preview and URL
    paymentImagePreview.value = URL.createObjectURL(file);
    formData.value.payment_proof_url = response.data.url;

    console.log('[ExpenseFormWithItems] Payment proof image uploaded successfully:', response.data.url);
    toast.success('Payment proof image uploaded successfully!');

    // Perform OCR on the uploaded image for bank transfer data
    await performOCROnPaymentProof(file);
  } catch (error) {
    console.error('Error uploading payment proof image:', error);
    toast.error('Failed to upload payment proof image');
  }
};

const removePaymentImage = () => {
  // Clear the file input
  if (paymentImageInput.value) {
    paymentImageInput.value.value = '';
  }

  // Clear the preview if it's a blob URL
  if (paymentImagePreview.value && paymentImagePreview.value.startsWith('blob:')) {
    URL.revokeObjectURL(paymentImagePreview.value);
  }

  // Reset all image-related values
  paymentImagePreview.value = null;
  formData.value.payment_proof_url = null;
};

const getPaymentProofUrl = computed(() => {
  if (paymentImagePreview.value) return paymentImagePreview.value;
  if (formData.value.payment_proof_url) {
    return getImageUrl(formData.value.payment_proof_url);
  }
  return '';
});

// OCR functionality for payment proof images using client-side Tesseract.js
const performOCROnPaymentProof = async (file) => {
  if (!formData.value.payment_method || formData.value.payment_method === 'cash') {
    return; // Only process OCR for online payments
  }

  try {
    isProcessingOCR.value = true;
    console.log('[ExpenseFormWithItems] Starting client-side OCR processing for payment proof...');

    // Import Tesseract.js dynamically to avoid bundling issues
    const { createWorker } = await import('tesseract.js');
    const worker = await createWorker('eng');
    
    // Create image URL for processing
    const imageUrl = URL.createObjectURL(file);
    
    // Process the image with OCR
    const { data: { text } } = await worker.recognize(imageUrl);
    console.log('[ExpenseFormWithItems] OCR extracted text:', text);
    
    // Clean up
    URL.revokeObjectURL(imageUrl);
    await worker.terminate();

    if (!text || text.trim().length === 0) {
      console.log('[ExpenseFormWithItems] No text extracted from OCR');
      toast.info('No readable text found in the image');
      return;
    }

    // Extract transaction reference and bank/platform name
    const transactionReference = extractTransactionReference(text);
    const bankName = extractBankName(text);
    
    console.log('[ExpenseFormWithItems] Extracted transaction reference:', transactionReference);
    console.log('[ExpenseFormWithItems] Extracted bank/platform name:', bankName);

    let extractedCount = 0;
    let extractedFields = [];

    // Auto-fill transaction reference if found and field is empty
    if (transactionReference) {
      if (!formData.value.transaction_reference) {
        formData.value.transaction_reference = transactionReference;
        extractedFields.push(`Reference: ${transactionReference}`);
        extractedCount++;
        console.log('[ExpenseFormWithItems] Auto-filled transaction reference:', transactionReference);
      } else {
        extractedFields.push(`Reference found: ${transactionReference} (field already filled)`);
      }
    }

    // Auto-fill bank/platform name if found and field is empty
    if (bankName) {
      if (!formData.value.online_platform) {
        formData.value.online_platform = bankName;
        extractedFields.push(`Platform: ${bankName}`);
        extractedCount++;
        console.log('[ExpenseFormWithItems] Auto-filled platform name:', bankName);
      } else {
        extractedFields.push(`Platform found: ${bankName} (field already filled)`);
      }
    }

    // Show appropriate success/info message
    if (extractedCount > 0) {
      const message = `Auto-filled ${extractedCount} field${extractedCount > 1 ? 's' : ''}: ${extractedFields.join(', ')}`;
      toast.success(message, { duration: 5000 });
    } else if (extractedFields.length > 0) {
      toast.info(extractedFields.join(', '));
    } else {
      toast.info('Payment proof processed - no transaction details found');
    }

  } catch (error) {
    console.error('[ExpenseFormWithItems] OCR processing failed:', error);
    toast.warning('Could not extract text from image - please enter details manually');
  } finally {
    isProcessingOCR.value = false;
  }
};



// Function to extract bank/platform name from OCR text (same as PaymentForm.vue)
const extractBankName = (text) => {
  // List of e-wallets and banks (e-wallets prioritized)
  const ewallets = [
    'GCash', 'Maya', 'PayMaya', 'GrabPay', 'Coins.ph'
  ];
  const banks = [
    'BPI', 'BDO', 'UnionBank', 'Metrobank', 'Landbank', 'PNB', 'Chinabank', 'EastWest',
    'Security Bank', 'RCBC', 'UCPB', 'PSBank', 'DBP', 'AUB', 'PBCom', 'Robinsons Bank', 'ING', 'GoTyme',
    'CIMB', 'Tonik', 'SeaBank', 'HSBC', 'Standard Chartered', 'Bank of Commerce'
  ];
  const cleanText = text.replace(/\s+/g, ' ').toLowerCase();
  
  // 1. Check for 'Sent via' or 'Transfer via' (case-insensitive)
  const viaMatch = text.match(/(?:sent|transfer) via ([a-zA-Z0-9 .]+)/i);
  if (viaMatch && viaMatch[1]) {
    // Try to match e-wallets first
    for (const ew of ewallets) {
      const regex = new RegExp(`\\b${ew}\\b`, 'i');
      if (regex.test(viaMatch[1])) {
        console.log('[ExpenseFormWithItems] Extracted e-wallet from via:', ew);
        return ew;
      }
    }
    // Then try banks
    for (const bank of banks) {
      const regex = new RegExp(`\\b${bank}\\b`, 'i');
      if (regex.test(viaMatch[1])) {
        console.log('[ExpenseFormWithItems] Extracted bank from via:', bank);
        return bank;
      }
    }
    // If not found, return the first word after 'via' as fallback
    const viaBank = viaMatch[1].trim().split(' ')[0];
    console.log('[ExpenseFormWithItems] Extracted bank name after "via":', viaBank);
    return viaBank;
  }
  
  // 2. Check for e-wallets in the whole text (word boundary)
  for (const ew of ewallets) {
    const regex = new RegExp(`\\b${ew.toLowerCase()}\\b`, 'i');
    if (regex.test(cleanText)) {
      console.log('[ExpenseFormWithItems] Extracted e-wallet from OCR:', ew);
      return ew;
    }
  }
  
  // 3. Check for banks in the whole text (word boundary)
  for (const bank of banks) {
    const regex = new RegExp(`\\b${bank.toLowerCase()}\\b`, 'i');
    if (regex.test(cleanText)) {
      console.log('[ExpenseFormWithItems] Extracted bank name from OCR:', bank);
      return bank;
    }
  }
  
  return null;
};

// Extract transaction reference from various bank transfer formats (same as PaymentForm.vue)
const extractTransactionReference = (text) => {
  // Clean up the text
  const cleanText = text.replace(/\s+/g, ' ').trim();
  console.log('[ExpenseFormWithItems] Cleaned OCR text for reference extraction:', cleanText);
  
  // High-priority patterns (most specific first) - these should match first
  const highPriorityPatterns = [
    // InstaPay/PESONet specific patterns (alphanumeric)
    /(?:Reference\s*no\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i, // InstaPay: Reference no. IT0250624085857007
    /(?:Reference\s*No\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i,
    /(?:InstaPay\s*Reference\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i,
    /(?:PESONet\s*Reference\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i,
    
    // BPI/Bank specific patterns
    /(?:Confirmation\s*No\.?\s*[:\-]?\s*)(\d{10,20})/i, // BPI: Confirmation No. 1750381383434
    /(?:Reference\s*Number\s*[:\-]?\s*)(\d{5,15})/i,    // Reference Number 00001139, 205144
    /(?:Reference\s*ID\s*[:\-]?\s*)([A-Za-z0-9]{8,20})/i, // Maya: Reference ID 6f5ee0aabbb3
    
    // GCash specific patterns (13-digit refs, with or without spaces)
    /(?:Ref\.?\s*No\.?\s*[:\-]?\s*)(\d{4}\s*\d{3}\s*\d{6})/i, // GCash with spaces: 1030 016 995414
    /(?:Ref\.?\s*No\.?\s*[:\-]?\s*)(\d{13})/i,               // GCash without spaces: 1030016995414 or 4029887933193
    /(?:Reference\.?\s*No\.?\s*[:\-]?\s*)(\d{4}\s*\d{3}\s*\d{6})/i,
    /(?:Reference\.?\s*No\.?\s*[:\-]?\s*)(\d{13})/i,
    /(?:GCash\s*Ref\.?\s*[:\-]?\s*)(\d{4}\s*\d{3}\s*\d{6})/i,
    /(?:GCash\s*Ref\.?\s*[:\-]?\s*)(\d{13})/i,
    
    // InstaPay with BN format (BDO)
    /(?:Reference\s*no\.?\s*[:\-]?\s*)(BN-\d{8}-\d{8})/i, // BN-20250616-58506651
    
    // Bank transfer reference patterns (numeric)
    /(?:Reference\s*No\.?\s*[:\-]?\s*)(\d{15,20})/i, // Bank example: Reference No. 202506200308229342
    /(?:Ref\.?\s*No\.?\s*[:\-]?\s*)(\d{15,20})/i,
    /(?:Transaction\s*Reference\.?\s*[:\-]?\s*)(\d{15,20})/i,
    /(?:Transfer\s*Reference\.?\s*[:\-]?\s*)(\d{15,20})/i,
    /(?:Confirmation\s*Number\.?\s*[:\-]?\s*)(\d{15,20})/i,
    
    // Maya patterns
    /(?:Transaction\s*ID\.?\s*[:\-]?\s*)(\d{12,15})/i,
    /(?:Maya\s*Ref\.?\s*[:\-]?\s*)(\d{12,15})/i,
    
    // PayPal patterns
    /(?:PayPal\s*Transaction\s*ID\.?\s*[:\-]?\s*)([A-Z0-9]{17})/i,
  ];
  
  // Medium-priority patterns (less specific)
  const mediumPriorityPatterns = [
    // Generic reference patterns (alphanumeric)
    /(?:Reference\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i,
    /(?:Transaction\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i,
    /(?:Ref\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i,
    
    // Generic reference patterns (numeric)
    /(?:Reference\.?\s*[:\-]?\s*)(\d{12,20})/i,
    /(?:Transaction\.?\s*[:\-]?\s*)(\d{12,20})/i,
    /(?:Ref\.?\s*[:\-]?\s*)(\d{12,20})/i,
    
    // Bank-specific patterns
    /(?:BPI\s*Reference\.?\s*[:\-]?\s*)([A-Z0-9]{8,25})/i,
    /(?:BDO\s*Reference\.?\s*[:\-]?\s*)([A-Z0-9]{8,25})/i,
    /(?:Metrobank\s*Reference\.?\s*[:\-]?\s*)([A-Z0-9]{8,25})/i,
    /(?:GoTyme\s*Reference\.?\s*[:\-]?\s*)([A-Z0-9]{8,25})/i,
  ];
  
  // Low-priority patterns (very generic - use as last resort)
  const lowPriorityPatterns = [
    // Long alphanumeric sequences (likely references)
    /\b([A-Z0-9]{15,25})\b/i, // Very long alphanumeric (like InstaPay)
    /\b([A-Z0-9]{12,14})\b/i, // Medium-long alphanumeric
    
    // Long numeric sequences (but exclude common account number lengths)
    /\b(\d{15,20})\b/, // Very long numbers (likely references)
    /\b(\d{13,14})\b/, // Medium-long numbers (GCash 13-digit refs)
    /\b(\d{12})\b/,    // 12-digit numbers
  ];
  
  // Patterns to EXCLUDE (account numbers, amounts, etc.)
  const excludePatterns = [
    /(?:Account\s*No\.?\s*[:\-]?\s*)(\d+)/i,        // Account numbers
    /(?:To\s*Account\s*No\.?\s*[:\-]?\s*)(\d+)/i,   // Recipient account
    /(?:From\s*Account\s*[:\-]?\s*)(\d+)/i,         // Source account
    /(?:Principal\s*Amount\s*[:\-]?\s*)(\d+)/i,     // Amounts
    /(?:Total\s*Amount\s*[:\-]?\s*)(\d+)/i,         // Total amounts
    /(?:Fee\s*[:\-]?\s*)(\d+)/i,                    // Fees
    /(?:Trace\s*No\.?\s*[:\-]?\s*)(\d+)/i,          // Trace numbers (usually short)
    /(?:Batch\s*No\.?\s*[:\-]?\s*)(\d+)/i,          // Batch numbers
  ];
  
  // Function to clean up extracted reference (remove spaces, normalize)
  const cleanReference = (reference) => {
    // Remove all spaces and normalize
    const cleaned = reference.replace(/\s+/g, '');
    console.log(`[ExpenseFormWithItems] Cleaned reference: "${reference}" -> "${cleaned}"`);
    return cleaned;
  };

  // Function to check if a number should be excluded
  const shouldExclude = (number, context = '') => {
    // Check against exclude patterns
    for (const pattern of excludePatterns) {
      if (pattern.test(context)) {
        console.log(`[ExpenseFormWithItems] Excluding "${number}" - matched exclude pattern: ${pattern}`);
        return true;
      }
    }
    
    // For GCash 13-digit references, be more lenient with length requirements
    if (/^\d{13}$/.test(number)) {
      console.log(`[ExpenseFormWithItems] Accepting 13-digit reference (likely GCash): "${number}"`);
      return false;
    }
    
    // Exclude references that are too short (likely account numbers or trace IDs)
    if (number.length < 10) {
      console.log(`[ExpenseFormWithItems] Excluding "${number}" - too short (${number.length} characters)`);
      return true;
    }
    
    // Exclude pure numeric sequences that are too short for modern references (except GCash 13-digit)
    if (/^\d+$/.test(number) && number.length < 12 && number.length !== 13) {
      console.log(`[ExpenseFormWithItems] Excluding "${number}" - numeric sequence too short for reference (${number.length} digits)`);
      return true;
    }
    
    // Exclude numbers that look like amounts (contain decimals in context)
    if (context.includes('.') && /\d+\.\d{2}/.test(context)) {
      console.log(`[ExpenseFormWithItems] Excluding "${number}" - appears to be an amount`);
      return true;
    }
    
    return false;
  };
  
  // Try high-priority patterns first
  console.log('[ExpenseFormWithItems] Trying high-priority patterns...');
  for (const pattern of highPriorityPatterns) {
    const matches = [...cleanText.matchAll(new RegExp(pattern.source, pattern.flags + 'g'))];
    for (const match of matches) {
      if (match && match[1]) {
        const rawReference = match[1].trim();
        const cleanedReference = cleanReference(rawReference);
        const context = match[0]; // Full match for context
        
        if (!shouldExclude(cleanedReference, context) && cleanedReference.length >= 10 && cleanedReference.length <= 25) {
          console.log(`[ExpenseFormWithItems] Found HIGH-PRIORITY transaction reference with pattern ${pattern}:`, cleanedReference);
          return cleanedReference;
        }
      }
    }
  }
  
  // Try medium-priority patterns
  console.log('[ExpenseFormWithItems] Trying medium-priority patterns...');
  for (const pattern of mediumPriorityPatterns) {
    const matches = [...cleanText.matchAll(new RegExp(pattern.source, pattern.flags + 'g'))];
    for (const match of matches) {
      if (match && match[1]) {
        const rawReference = match[1].trim();
        const cleanedReference = cleanReference(rawReference);
        const context = match[0];
        
        if (!shouldExclude(cleanedReference, context) && cleanedReference.length >= 10 && cleanedReference.length <= 25) {
          console.log(`[ExpenseFormWithItems] Found MEDIUM-PRIORITY transaction reference with pattern ${pattern}:`, cleanedReference);
          return cleanedReference;
        }
      }
    }
  }
  
  // Try low-priority patterns (last resort)
  console.log('[ExpenseFormWithItems] Trying low-priority patterns...');
  for (const pattern of lowPriorityPatterns) {
    const matches = [...cleanText.matchAll(new RegExp(pattern.source, pattern.flags + 'g'))];
    for (const match of matches) {
      if (match && match[1]) {
        const rawReference = match[1].trim();
        const cleanedReference = cleanReference(rawReference);
        
        // For low-priority patterns, be extra careful about exclusions
        const contextStart = Math.max(0, match.index - 50);
        const contextEnd = Math.min(cleanText.length, match.index + match[0].length + 50);
        const context = cleanText.substring(contextStart, contextEnd);
        
        if (!shouldExclude(cleanedReference, context) && cleanedReference.length >= 12 && cleanedReference.length <= 25) {
          console.log(`[ExpenseFormWithItems] Found LOW-PRIORITY transaction reference with pattern ${pattern}:`, cleanedReference);
          console.log(`[ExpenseFormWithItems] Context: "${context}"`);
          return cleanedReference;
        }
      }
    }
  }
  
  console.log('[ExpenseFormWithItems] No valid transaction reference found');
  return null;
};



// Global paste event handler for accessibility
const handleGlobalPaste = (event) => {
  // Check if user is typing in an input field - if so, don't interfere
  const activeElement = document.activeElement;
  if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
    return;
  }
  
  console.log('Global paste event detected in ExpenseFormWithItems');
  
  // Check if we have focus on either image area
  const receiptArea = document.querySelector('[ref="receiptPasteArea"]');
  const paymentArea = document.querySelector('[ref="paymentPasteArea"]');
  
  if (receiptArea && (receiptArea === activeElement || receiptArea.contains(activeElement))) {
    handleReceiptImagePaste(event);
  } else if (paymentArea && (paymentArea === activeElement || paymentArea.contains(activeElement))) {
    handlePaymentImagePaste(event);
  } else {
    // If not focused on a specific area, try receipt first (more commonly used)
    handleReceiptImagePaste(event);
  }
};

onMounted(async () => {
  try {
    // Load suppliers
    const suppliersResponse = await api.get('/suppliers');
    suppliers.value = suppliersResponse.data;
    
    // Initialize filtered suppliers with all suppliers
    filteredSuppliers.value = suppliers.value;

    // Load expense groups
    const groupsResponse = await api.get('/expenses/expense-groups');
    expenseGroups.value = groupsResponse.data;

    // Load departments
    const departmentsResponse = await api.get('/departments');
    departments.value = departmentsResponse.data;

    console.log('[ExpenseFormWithItems] Initializing with expense:', props.expense);

    if (props.expense) {
      editMode.value = true;
      const expenseDate = props.expense.expense_date ? props.expense.expense_date.split('T')[0] : new Date().toISOString().split('T')[0];
      console.log('[ExpenseFormWithItems] Setting expense date:', expenseDate);
      
      formData.value = {
        expense_group_id: props.expense.expense_group_id,
        department_id: props.expense.department_id,
        supplier_id: props.expense.supplier_id,
        expense_date: expenseDate,
        receipt_image_url: props.expense.receipt_image_url,
        payment_proof_url: props.expense.payment_proof_url,
        notes: props.expense.notes || '',
        payment_method: props.expense.payment_method || '',
        online_platform: props.expense.online_platform || '',
        transaction_reference: props.expense.transaction_reference || '',
        items: props.expense.items || []
      };
      
      // Set supplier search value to supplier name if editing
      if (formData.value.supplier_id) {
        const selectedSupplier = suppliers.value.find(s => s.id === formData.value.supplier_id);
        if (selectedSupplier) {
          supplierSearch.value = selectedSupplier.name;
        }
        await loadSupplierProducts();
      }
    }
    
    // Add global paste event listener for enhanced accessibility
    document.addEventListener('paste', handleGlobalPaste);
    
    // Add click event listener to close dropdown when clicking outside
    document.addEventListener('click', closeSupplierDropdown);
    
    console.log('Global paste event listener added for ExpenseFormWithItems');
  } catch (error) {
    console.error('[ExpenseFormWithItems] Error loading form data:', error);
    toast.error('Failed to load form data');
  }
});

// Cleanup event listeners to prevent memory leaks
onBeforeUnmount(() => {
  document.removeEventListener('paste', handleGlobalPaste);
  document.removeEventListener('click', closeSupplierDropdown);
  console.log('Global paste event listener removed from ExpenseFormWithItems');
  
  // Clean up blob URLs to prevent memory leaks
  if (imagePreview.value && imagePreview.value.startsWith('blob:')) {
    URL.revokeObjectURL(imagePreview.value);
  }
  
  if (paymentImagePreview.value && paymentImagePreview.value.startsWith('blob:')) {
    URL.revokeObjectURL(paymentImagePreview.value);
  }
});

const handleSubmit = async () => {
  // Prevent duplicate submissions
  if (isSubmitting.value) {
    console.log('[ExpenseFormWithItems] Submission already in progress, ignoring duplicate click');
    return;
  }
  
  try {
    // Set submission flags to prevent multiple submissions
    isSubmitting.value = true;
    submitDisabled.value = true;
    
    if (formData.value.items.length === 0) {
      toast.error('Please add at least one item');
      submitDisabled.value = false;
      isSubmitting.value = false;
      return;
    }

    // Check for receipt image
    if (!formData.value.receipt_image_url) {
      toast.error('Receipt image is required for all expenses');
      submitDisabled.value = false;
      isSubmitting.value = false;
      return;
    }

    // Validate payment method fields if online payment is selected
    if (formData.value.payment_method === 'online') {
      if (!formData.value.online_platform) {
        toast.error('Payment platform is required for online payments');
        submitDisabled.value = false;
        isSubmitting.value = false;
        return;
      }
      if (!formData.value.transaction_reference) {
        toast.error('Transaction reference is required for online payments');
        submitDisabled.value = false;
        isSubmitting.value = false;
        return;
      }
      if (!formData.value.payment_proof_url) {
        toast.error('Payment proof image is required for online payments');
        submitDisabled.value = false;
        isSubmitting.value = false;
        return;
      }
    }

    // Set description to the first line of notes for the backend, or a default value if notes is empty
    const description = formData.value.notes ? formData.value.notes.split('\n')[0] : 'Expense';
    
    // Fix: Ensure we send the date exactly as selected without any timezone conversion
    const dataToSubmit = {
      ...formData.value,
      description,
      expense_date: formData.value.expense_date
    };

    console.log('[ExpenseFormWithItems] Submitting form data:', dataToSubmit);
    emit('submit', dataToSubmit);
    
    // Keep the button disabled for a short period even on success
    // to prevent rapid repeated submissions
    setTimeout(() => {
      submitDisabled.value = false;
    }, 2000);
  } catch (error) {
    console.error('[ExpenseFormWithItems] Error in form submission:', error);
    submitDisabled.value = false;
    isSubmitting.value = false;
    throw error;
  }
};
</script> 